import { Card } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  CustomerPortalBillGroup,
  CustomerPortalData,
  CustomerPortalSessionData,
} from '@monetize/types/app';
import { ReactNode, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { useUpdateCustomerPortalBillGroup } from '../../../../api/customerPortalService';
import {
  MBox,
  MButton,
  MCard,
  MFlex,
  MFormField,
  MGrid,
  MInput,
  MText,
} from '../../../../components/Monetize';

const BillingDetailsSchema = z.object({
  purchaseOrderNumber: z.string(),
  vatNumber: z.string(),
  registrationNumber: z.string(),
});

type BillingDetailsForm = z.infer<typeof BillingDetailsSchema>;

interface BillingDetailsCardProps {
  sessionData: CustomerPortalSessionData;
  billGroup: CustomerPortalBillGroup;
  permissions: CustomerPortalData['permissions']['billingSummary'];
}

const normalizeNullable = (value: string): string | null => {
  const next = value.trim();
  return next ? next : null;
};

const FieldRow = ({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) => (
  <MBox>
    <MText fontSize="xs" color="fg.muted" fontWeight={500} mb={1}>
      {label}
    </MText>
    {children}
  </MBox>
);

export const BillingDetailsCard = ({
  sessionData,
  billGroup,
  permissions,
}: BillingDetailsCardProps) => {
  const hasContent =
    permissions.purchaseOrderNumber.view ||
    permissions.vatNumber.view ||
    permissions.registrationNumber.view;

  const canEdit =
    permissions.edit &&
    (permissions.purchaseOrderNumber.edit ||
      permissions.vatNumber.edit ||
      permissions.registrationNumber.edit);

  const [isEditing, setIsEditing] = useState(false);

  const formState = useForm<BillingDetailsForm>({
    resolver: zodResolver(BillingDetailsSchema),
    mode: 'onSubmit',
    defaultValues: {
      purchaseOrderNumber: billGroup.purchaseOrderNumber || '',
      vatNumber: billGroup.vatNumber || '',
      registrationNumber: billGroup.registrationNumber || '',
    },
  });

  useEffect(() => {
    if (!isEditing) {
      formState.reset({
        purchaseOrderNumber: billGroup.purchaseOrderNumber || '',
        vatNumber: billGroup.vatNumber || '',
        registrationNumber: billGroup.registrationNumber || '',
      });
    }
  }, [
    billGroup.purchaseOrderNumber,
    billGroup.registrationNumber,
    billGroup.vatNumber,
    formState,
    isEditing,
  ]);

  const { mutateAsync: updateBillGroup, isPending } =
    useUpdateCustomerPortalBillGroup({
      meta: { showErrorToast: true },
    });

  const submit = async (values: BillingDetailsForm) => {
    const nextPo = normalizeNullable(values.purchaseOrderNumber);
    const nextVat = normalizeNullable(values.vatNumber);
    const nextRegistration = normalizeNullable(values.registrationNumber);

    const currentPo = billGroup.purchaseOrderNumber || null;
    const currentVat = billGroup.vatNumber || null;
    const currentRegistration = billGroup.registrationNumber || null;

    const payload: {
      id: string;
      purchaseOrderNumber?: string | null;
      vatNumber?: string | null;
      registrationNumber?: string | null;
    } = {
      id: billGroup.id,
    };

    if (permissions.purchaseOrderNumber.edit && nextPo !== currentPo) {
      payload.purchaseOrderNumber = nextPo;
    }
    if (permissions.vatNumber.edit && nextVat !== currentVat) {
      payload.vatNumber = nextVat;
    }
    if (
      permissions.registrationNumber.edit &&
      nextRegistration !== currentRegistration
    ) {
      payload.registrationNumber = nextRegistration;
    }

    if (Object.keys(payload).length === 1) {
      setIsEditing(false);
      return;
    }

    await updateBillGroup({ sessionData, payload });
    setIsEditing(false);
  };

  const handleCancel = () => {
    formState.reset({
      purchaseOrderNumber: billGroup.purchaseOrderNumber || '',
      vatNumber: billGroup.vatNumber || '',
      registrationNumber: billGroup.registrationNumber || '',
    });
    setIsEditing(false);
  };

  if (!hasContent) {
    return null;
  }

  return (
    <MCard variant="border" borderRadius="md">
      <Card.Header pt={4} pb={2} px={4}>
        <MFlex justifyContent="space-between" alignItems="center">
          <MText fontWeight="bold" fontSize="lg" color="tPurple.dark">
            Billing Details
          </MText>
          {canEdit && !isEditing && (
            <MButton
              variant="tertiary"
              size="sm"
              onClick={() => setIsEditing(true)}
            >
              Edit
            </MButton>
          )}
        </MFlex>
      </Card.Header>
      <Card.Body pt={2} pb={4} px={4}>
        {isEditing ? (
          <MGrid
            templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
            gap={4}
          >
            {permissions.purchaseOrderNumber.view && (
              <MFormField label="Purchase Order Number">
                <Controller
                  control={formState.control}
                  name="purchaseOrderNumber"
                  render={({ field }) => (
                    <MInput
                      {...field}
                      variant={
                        permissions.purchaseOrderNumber.edit
                          ? 'outline'
                          : 'readonly'
                      }
                      isReadOnly={!permissions.purchaseOrderNumber.edit}
                    />
                  )}
                />
              </MFormField>
            )}
            {permissions.vatNumber.view && (
              <MFormField label="VAT Number">
                <Controller
                  control={formState.control}
                  name="vatNumber"
                  render={({ field }) => (
                    <MInput
                      {...field}
                      variant={
                        permissions.vatNumber.edit ? 'outline' : 'readonly'
                      }
                      isReadOnly={!permissions.vatNumber.edit}
                    />
                  )}
                />
              </MFormField>
            )}
            {permissions.registrationNumber.view && (
              <MFormField label="Registration Number">
                <Controller
                  control={formState.control}
                  name="registrationNumber"
                  render={({ field }) => (
                    <MInput
                      {...field}
                      variant={
                        permissions.registrationNumber.edit
                          ? 'outline'
                          : 'readonly'
                      }
                      isReadOnly={!permissions.registrationNumber.edit}
                    />
                  )}
                />
              </MFormField>
            )}
          </MGrid>
        ) : (
          <MGrid
            templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
            gap={4}
          >
            {permissions.purchaseOrderNumber.view && (
              <FieldRow label="Purchase Order Number">
                <MText fontSize="sm" color="fg.primary">
                  {billGroup.purchaseOrderNumber || '—'}
                </MText>
              </FieldRow>
            )}
            {permissions.vatNumber.view && (
              <FieldRow label="VAT Number">
                <MText fontSize="sm" color="fg.primary">
                  {billGroup.vatNumber || '—'}
                </MText>
              </FieldRow>
            )}
            {permissions.registrationNumber.view && (
              <FieldRow label="Registration Number">
                <MText fontSize="sm" color="fg.primary">
                  {billGroup.registrationNumber || '—'}
                </MText>
              </FieldRow>
            )}
          </MGrid>
        )}
      </Card.Body>
      {isEditing && (
        <Card.Footer pt={2} pb={4} px={4}>
          <MFlex justifyContent="flex-end" gap={2} w="100%">
            <MButton
              variant="cancel"
              onClick={handleCancel}
              isDisabled={isPending}
            >
              Cancel
            </MButton>
            <MButton
              variant="primary"
              onClick={formState.handleSubmit(submit)}
              isLoading={isPending}
              isDisabled={isPending}
            >
              Save
            </MButton>
          </MFlex>
        </Card.Footer>
      )}
    </MCard>
  );
};
